import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ShoppingBag, Check } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { isContactSaved, parseContact, saveContact } from "@/lib/saveContact";

const SHOWN_KEY = "audrivio_exit_shown";
const MIN_TIME_MS = 8000;

// Shows once per session when a visitor with items in cart is about to leave
// (desktop: mouse to top edge; mobile: quick scroll back up).
export default function ExitIntentPopup() {
  const { pathname } = useLocation();
  const { items, subtotal, setOpen: setCartOpen } = useCart();
  const { customer } = useCustomerAuth() || {};
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const eligible = items.length > 0 && !customer && !pathname.startsWith("/checkout") && !pathname.startsWith("/admin");

  useEffect(() => {
    if (!eligible || isContactSaved()) return;
    try { if (sessionStorage.getItem(SHOWN_KEY)) return; } catch { /* ignore */ }
    const start = Date.now();
    const fire = () => {
      if (Date.now() - start < MIN_TIME_MS || isContactSaved()) return;
      try { if (sessionStorage.getItem(SHOWN_KEY)) return; sessionStorage.setItem(SHOWN_KEY, "1"); } catch { /* ignore */ }
      setCartOpen(false);
      setOpen(true);
    };
    const onMouseOut = (e) => { if (!e.relatedTarget && e.clientY <= 5) fire(); };
    let lastY = window.scrollY; let lastT = Date.now();
    const onScroll = () => {
      const now = Date.now(); const y = window.scrollY;
      if (window.innerWidth < 1024 && lastY - y > 250 && now - lastT < 400) fire();
      lastY = y; lastT = now;
    };
    document.addEventListener("mouseout", onMouseOut);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { document.removeEventListener("mouseout", onMouseOut); window.removeEventListener("scroll", onScroll); };
  }, [eligible]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    const c = parseContact(value);
    if (!c) { setError(t("save_cart_invalid")); return; }
    setState("loading"); setError("");
    try { await saveContact({ ...c, items, subtotal, lang, source: "exit_intent" }); setState("done"); }
    catch { setState("idle"); setError(t("save_cart_invalid")); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="exit-intent-popup" className="sm:max-w-md">
        <div className="mx-auto w-12 h-12 rounded-full bg-orange-50 flex items-center justify-center"><ShoppingBag className="w-6 h-6 text-xd-orange" /></div>
        <DialogTitle className="text-center font-display text-xl">{t("exit_title")}</DialogTitle>
        <DialogDescription className="text-center text-sm">{t("exit_desc")}</DialogDescription>
        {state === "done" ? (
          <div data-testid="exit-intent-done" className="flex items-center justify-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-700"><Check className="w-4 h-4" /> {t("exit_done")}</div>
        ) : (
          <form onSubmit={submit} className="space-y-2">
            <input autoFocus value={value} onChange={(e) => setValue(e.target.value)} placeholder={t("save_cart_placeholder")} data-testid="exit-intent-input" className="w-full rounded-md border border-xd-border px-3 py-2.5 text-sm" />
            {error && <div data-testid="exit-intent-error" className="text-xs text-red-600">{error}</div>}
            <button type="submit" disabled={state === "loading"} data-testid="exit-intent-submit" className="w-full btn-cta text-white h-11 rounded-lg text-sm font-semibold disabled:opacity-50">{t("exit_btn")}</button>
            <button type="button" onClick={() => setOpen(false)} data-testid="exit-intent-dismiss" className="w-full text-xs text-xd-muted hover:text-xd-text py-1">{t("exit_dismiss")}</button>
            <p className="text-[10px] text-center text-xd-muted">{t("save_cart_consent")}</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
